import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { styled } from '@mui/material'
import WidgetsIcon from '@mui/icons-material/Widgets'
import { AccordionDetails, AccordionSummary, Box, IconButton, Typography, type SvgIconTypeMap } from '@mui/material'
import Accordion from '@mui/material/Accordion'
import { type OverridableComponent } from '@mui/material/OverridableComponent'
import { InvoiceCloudFrame } from 'components/domain'
import { MButton } from 'components/ui'
import { MESSAGES, PAY_TOOLS } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { isNil } from 'lodash'
import { OverflowList } from 'react-overflow-list'
interface IQuickLinks {
    title: string
    Icon: OverridableComponent<SvgIconTypeMap<Record<string, unknown>, 'svg'>>
    page?: number
    link?: string
}

const QuickLinks = (): JSX.Element => {
    const isMobile = useMobileBreakpoints()
    const { account } = useAuth()
    const { enqueueSnackbar } = useSnackbar()
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const handleItem = (item: IQuickLinks): void => {
        if (item.title === 'View Bill') {
            handleDownloadBill()
        }
    }
    const [isExpanded, setIsExpanded] = useState<boolean>(false)

    const handleAccordionOnChange = (_: any, expanded: any): void => {
        setIsExpanded(expanded)
    }
    const handleDownloadBill = (): void => {
        if (!isNil(account?.myAccount?.billDisplayURL)) {
            window.open(account?.myAccount?.billDisplayURL, '_blank', 'noopener,noreferrer')
        } else {
            enqueueSnackbar(MESSAGES.BILL_PDF_ERROR, {
                variant: 'error',
                autoHideDuration: 5000
            })
        }
    }

    const MobileView = () => {
        return (
            <Box mt={2}>
                <Accordion onChange={handleAccordionOnChange} expanded={isExpanded}>
                    <AccordionSummary
                        expandIcon={
                            <IconButton
                                disableRipple
                                size="small"
                                sx={{ bgcolor: color.grey[200] }}
                            >
                                {isExpanded
                                    ? (
                                        <RemoveIcon sx={{ color: color.primary[600] }} />
                                    )
                                    : (
                                        <AddIcon sx={{ color: color.primary[600] }} />
                                    )}
                            </IconButton>
                        }
                    >
                        <Box display="flex" gap={1} p={0.5}>
                            <WidgetsIcon sx={{ color: color.primary[600] }} />
                            <Typography >Quick Links</Typography>
                        </Box>

                    </AccordionSummary>
                    <AccordionDetails>
                        {PAY_TOOLS.map((tool, index) => (
                            <InvoiceCloudFrame key={index} page={tool.page}>
                                <MButton
                                    key={index}
                                    variant="contained"
                                    size="medium"
                                    texttransform='none'
                                    bgcolor={color.background[50]}
                                    sx={{
                                        color: color.grey[800],
                                        paddingX: 3,
                                        paddingY: 1.5,
                                        marginBottom: 1.5,
                                        justifyContent: 'left'
                                    }}
                                    fullWidth
                                    startIcon={<tool.Icon sx={{ color: color.primary[700] }} />}
                                    onClick={() => {
                                        handleItem(tool)
                                    }}
                                >
                                    {tool.title}
                                </MButton>
                            </InvoiceCloudFrame>
                        ))}
                    </AccordionDetails>

                </Accordion>
            </Box>
        )
    }

    const ItemRenderer = (tool: any, index: any) => {
        return (
            <InvoiceCloudFrame key={index} page={tool.page}>
                <MButton
                    key={index}
                    texttransform="none"
                    rounded="true"
                    size="large"
                    sx={{
                        background: color.background[100],
                        boxShadow: 'none',
                        color: color.grey[800],
                        px: 2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden'
                    }}
                    startIcon={<tool.Icon sx={{ color: color.primary[700] }} />}
                    onClick={() => {
                        handleItem(tool)
                    }}
                >
                    {tool.title}
                </MButton>
            </InvoiceCloudFrame>
        )
    }

    const handleMouseOver = () => {
        setIsDropdownOpen(true)
    }

    const handleMouseOut = (event: React.MouseEvent) => {
        const related = event.relatedTarget as HTMLElement | null
        const current = event.currentTarget as HTMLElement

        if (!current.contains(related)) {
            setIsDropdownOpen(false)
        }
    }

    const OverflowRenderer = (items: any) => {
        return (
            <Box position="relative" onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
                <MButton
                    texttransform="none"
                    rounded="true"
                    size="large"
                    sx={{
                        background: color.background[100],
                        boxShadow: 'none',
                        color: color.grey[800],
                        px: 3,
                        whiteSpace: 'nowrap'
                    }}
                    startIcon={<AddIcon sx={{ color: color.primary[700] }} />}
                >
                    More
                </MButton>
                {isDropdownOpen && (
                    <Box
                        position="absolute"
                        top="100%"
                        right={0}
                        bgcolor={color.background[50]}
                        boxShadow={3}
                        borderRadius={1}
                        py={1}
                        zIndex={9999}
                        width="max-content"
                    >
                        {items.map((tool: any, index: number) => {
                            const content = (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 2,
                                        py: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: color.grey[100]
                                        }
                                    }}
                                    onClick={() => { handleItem(tool) }}
                                >
                                    <tool.Icon sx={{ color: color.primary[700], fontSize: 20 }} />
                                    <Typography color={color.grey[800]}>
                                        {tool.title}
                                    </Typography>
                                </Box>
                            )
                            return tool.title === 'View Bill'
                                ? <Box key={index}>{content}</Box>
                                : (
                                    <InvoiceCloudFrame key={index} page={tool.page}>
                                        {content}
                                    </InvoiceCloudFrame>
                                )
                        })}
                    </Box>
                )}
            </Box>
        )
    }

    const DesktopView = () => {
        return (
            <Box display="flex" width="100%" justifyContent="flex-start" alignItems="center" py={3} px={5} gap={2}>
                <Typography whiteSpace="nowrap" overflow="hidden" color={color.grey[700]} fontSize={12} fontWeight={500} letterSpacing={2}>
                    QUICK LINKS
                </Typography>
                <CollapsibleMenu
                    collapseFrom="end"
                    minVisibleItems={0}
                    items={PAY_TOOLS}
                    itemRenderer={ItemRenderer}
                    overflowRenderer={OverflowRenderer}
                />
            </Box>
        )
    }

    return (isMobile
        ? <MobileView />
        : <DesktopView />
    )
}

const CollapsibleMenu = styled(OverflowList)`
  gap: 8px;
`

export default QuickLinks
